/**
 * P4 — in-page sub-nav shared across the client-report agency surfaces.
 *
 * Each tab is gated on the capability that guards its page, so a team_lead
 * (no branding) or analyst (review-only) sees exactly the tabs they can reach.
 * Rendered at the top of each sub-page under the client-reports area. The
 * sidebar also carries capability-guarded entries; this strip keeps the pages
 * navigable once the user is inside the area.
 */
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCan } from '../../lib/rbac';
import './client-reports.scss';

// The client-inputs API shipped to staging + live in the 2026-07-23 release,
// so the DEV-first hold (see route.ts portal flags) is over.
const INBOX_ENABLED = true;

interface TabDef {
  to: string;
  label: string;
  show: boolean;
  end?: boolean;
}

const ClientReportsNav = () => {
  const { t } = useTranslation();
  const canReview = useCan('portal.reports.review');
  const canKpis = useCan('portal.kpis.manage');
  const canBranding = useCan('portal.branding.manage');
  const canSettings = useCan('portal.settings.manage');
  const canClients = useCan('portal.clients.manage');

  // "Reports" lists the drafted reports (open one in the workbench). Client
  // invite/management moved to the Team page (`portal.clients.manage`).
  // Managed Agents moved out of Reporting to the Skills area (`/skills`).
  const tabs: TabDef[] = [
    {
      to: '/client-reports/reports',
      label: t('Reports', 'Reports'),
      show: canReview,
    },
    {
      to: '/client-reports/inbox',
      label: t('ClientReportsNavInbox', 'Client inbox'),
      show: canClients && INBOX_ENABLED,
    },
    {
      to: '/client-reports/targets',
      label: t('ClientReportsTargets', 'KPI targets'),
      show: canKpis,
    },
    {
      to: '/client-reports/branding',
      label: t('ClientReportsNavBranding', 'Branding'),
      show: canBranding,
    },
    {
      to: '/client-reports/settings',
      label: t('ClientReportsSettings', 'Portal settings'),
      show: canSettings,
    },
  ];

  const visible = tabs.filter((tab) => tab.show);
  if (visible.length <= 1) return null;

  return (
    <nav className='client-reports__subnav'>
      {visible.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            `client-reports__subnav-link${
              isActive ? ' client-reports__subnav-link--active' : ''
            }`
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
};

export default ClientReportsNav;
